import React, { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Home, Trophy, Ticket, Search, Bell, Wallet, User,
    ChevronRight, Star, Monitor, Swords, Activity,
    Dribbble, Target, Trophy as TrophyIcon, Zap, Plus
} from 'lucide-react';
import Footer from '../components/Footer';
import LiveChatWidget from '../components/LiveChatWidget';
import { getSportIcon } from '../utils/sportsUtils';
import { SPORTS_CONFIG } from '../config/constants';

const CasinoIcon = ({ size = 24, className = "", strokeWidth = 2 }) => {
    const effectiveStrokeWidth = strokeWidth * 3.2;
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width={size} height={size} className={className} fill="none">
            <g stroke="currentColor" fill="none" strokeWidth={effectiveStrokeWidth} strokeLinecap="round" strokeLinejoin="round">
                <path d="M 15 185 H 185"></path>
                <rect x="25" y="60" width="130" height="115" rx="8"></rect>
                <path d="M 25 130 H 155"></path>
                <path d="M 35 60 V 45 A 5 5 0 0 1 40 40 H 140 A 5 5 0 0 1 145 45 V 60"></path>
                <rect x="75" y="30" width="30" height="10"></rect>
                <path d="M 80 30 V 20 A 10 10 0 0 1 100 20 V 30"></path>
                <path d="M 90 25 V 15"></path>
                <circle cx="45" cy="50" r="2.5" fill="currentColor" stroke="none"></circle>
                <circle cx="63" cy="50" r="2.5" fill="currentColor" stroke="none"></circle>
                <circle cx="81" cy="50" r="2.5" fill="currentColor" stroke="none"></circle>
                <circle cx="99" cy="50" r="2.5" fill="currentColor" stroke="none"></circle>
                <circle cx="117" cy="50" r="2.5" fill="currentColor" stroke="none"></circle>
                <circle cx="135" cy="50" r="2.5" fill="currentColor" stroke="none"></circle>
                <rect x="40" y="75" width="100" height="40" rx="4"></rect>
                <rect x="46" y="80" width="24" height="30" rx="2"></rect>
                <rect x="78" y="80" width="24" height="30" rx="2"></rect>
                <rect x="110" y="80" width="24" height="30" rx="2"></rect>
                <path d="M 50 88 H 66 L 55 105"></path>
                <path d="M 82 88 H 98 L 87 105"></path>
                <path d="M 114 88 H 130 L 119 105"></path>
                <rect x="40" y="140" width="12" height="25" rx="3"></rect>
                <path d="M 46 146 V 159"></path>
                <path d="M 65 175 V 145 H 115 V 175"></path>
                <rect x="68" y="166" width="20" height="6" rx="1.5"></rect>
                <rect x="68" y="158" width="20" height="6" rx="1.5"></rect>
                <rect x="92" y="168" width="18" height="5" rx="1.5"></rect>
                <circle cx="135" cy="155" r="8"></circle>
                <path d="M 155 155 H 168 V 135 H 155"></path>
                <path d="M 168 150 H 173 V 140 H 168"></path>
                <path d="M 173 145 L 173 90"></path>
                <circle cx="173" cy="80" r="10"></circle>
            </g>
        </svg>
    );
};

const SportsIcon = ({ size = 24, className = "", strokeWidth = 2 }) => {
    const thickStroke = strokeWidth * 3;
    const thinStroke = strokeWidth * 2;
    return (
        <svg width={size} height={size} viewBox="0 0 100 150" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M20 145 H80 Q85 145 85 140 V135 Q85 130 80 130 H20 Q15 130 15 135 V140 Q15 145 20 145 Z" stroke="currentColor" strokeWidth={thickStroke} strokeLinecap="round" strokeLinejoin="round" />
            <path d="M25 130 L25 135" stroke="currentColor" strokeWidth={thickStroke} strokeLinecap="round" />
            <path d="M75 130 L75 135" stroke="currentColor" strokeWidth={thickStroke} strokeLinecap="round" />
            <path d="M30 130 C30 100 20 80 15 65" stroke="currentColor" strokeWidth={thickStroke} strokeLinecap="round" />
            <path d="M70 130 C70 100 80 80 85 65" stroke="currentColor" strokeWidth={thickStroke} strokeLinecap="round" />
            <path d="M45 130 C45 110 60 90 60 78" stroke="currentColor" strokeWidth={thickStroke} strokeLinecap="round" />
            <circle cx="50" cy="45" r="38" stroke="currentColor" strokeWidth={thickStroke} fill="transparent" />
            <g stroke="currentColor" strokeWidth={thinStroke}>
                <path d="M50 25 L62 33 L58 48 L42 48 L38 33 Z" fill="currentColor" fillOpacity="0.2" />
                <path d="M50 25 L50 7" />
                <path d="M62 33 L78 28" />
                <path d="M58 48 L70 60" />
                <path d="M42 48 L30 60" />
                <path d="M38 33 L22 28" />
                <path d="M78 28 L85 40" />
                <path d="M22 28 L15 40" />
                <path d="M30 60 L40 75" />
                <path d="M70 60 L60 75" />
            </g>
        </svg>
    );
};

const UserLayout = ({ children, activeTab, setActiveTab, userProfile, activeCurrency, user, onLoginRequest }) => {
    const scrollRef = useRef(null);
    const { pathname } = useLocation();

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo(0, 0);
        }
    }, [pathname]);

    const navItems = [
        { id: 'home', icon: Home, label: 'Inicio' },
        { id: 'sports', icon: SportsIcon, label: 'Deportes' },
        { id: 'mybets', icon: Ticket, label: 'Mis Apuestas' },
        { id: 'casino', icon: CasinoIcon, label: 'Casino' },
        { id: 'profile', icon: User, label: 'Perfil' }
    ];

    const popularSports = SPORTS_CONFIG.filter(s => s.popular).map(s => ({
        ...s,
        label: s.name,
        Icon: getSportIcon(s)
    }));

    const otherSports = SPORTS_CONFIG.filter(s => !s.popular).map(s => ({
        ...s,
        label: s.name,
        Icon: getSportIcon(s)
    }));

    const currentBalance = activeCurrency === 'USDT'
        ? Number(userProfile?.balanceUsdt || 0)
        : (Number(userProfile?.balanceBs || 0) + Number(userProfile?.bonusBalance || 0));

    return (
        <div className="h-[100dvh] bg-[#171717] flex flex-col font-sans text-neutral-100 overflow-hidden relative">

            {/* 1. DESKTOP TOP NAV */}
            {/* 1. DESKTOP TOP NAV */}
            <nav className="hidden lg:flex h-20 bg-[#171717] border-b border-white/5 px-8 items-center justify-between sticky top-0 z-[1000] shrink-0 relative">
                {/* Logo Area */}
                <div className="flex items-center gap-2 w-[240px]"> {/* Fixed width to prevent shift */}
                    <span className="text-[20px] font-black text-white italic tracking-tighter cursor-pointer" onClick={() => setActiveTab('home')}>
                        PRIME<span className="text-[#F59E0B]">APUESTA</span>
                    </span>
                </div>

                {/* Desktop Main Nav - Absolute Center */}
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-8">
                    {navItems.filter(i => i.id !== 'profile').map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${activeTab === item.id ? 'text-[#F59E0B] bg-white/5 shadow-inner' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}
                        >
                            <item.icon size={18} strokeWidth={activeTab === item.id ? 2.5 : 2} className="mb-0.5" />
                            <span className="text-sm font-bold tracking-tight">{item.label}</span>
                        </button>
                    ))}
                </div>

                {/* Right Actions */}
                <div className="flex items-center justify-end gap-5 w-[240px]">
                    {user ? (
                        <>
                            {/* Balance & Deposit Container - Rectangular/Rounded Style */}
                            <div className="flex items-center bg-[#121212] border border-white/5 rounded-xl p-1.5 pl-4 pr-1.5 shadow-sm group hover:border-white/10 transition-colors">
                                <div className="flex flex-col items-end mr-3 min-w-[80px]">
                                    <span className="text-[10px] font-black text-[#F59E0B] tracking-tight leading-none mb-0.5">{activeCurrency || 'Saldo'}</span>
                                    <span className="text-sm font-black text-white font-mono tracking-tight leading-none">
                                        {activeCurrency === 'USDT' ? '₮' : 'Bs'} {currentBalance.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <button
                                    onClick={() => setActiveTab('deposit')}
                                    className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black text-sm font-black uppercase tracking-wide px-6 py-2.5 rounded-lg transition-all active:scale-95 shadow-lg shadow-[#F59E0B]/10"
                                >
                                    DEPOSITAR
                                </button>
                            </div>

                            {/* Icons & Profile Group */}
                            <div className="flex items-center gap-3 pl-2">
                                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 transition-all active:scale-95 relative">
                                    <Bell size={18} />
                                    <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 bg-red-500 rounded-full shadow-[0_0_0_2px_#171717]"></span>
                                </button>

                                {/* Profile Snippet - Squircle with + Badge */}
                                <button onClick={() => setActiveTab('profile')} className="relative group active:scale-95 transition-transform">
                                    <div className="w-10 h-10 rounded-2xl bg-neutral-900 border border-white/10 overflow-hidden group-hover:border-[#F59E0B] transition-all shadow-lg">
                                        <img
                                            src={user?.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userProfile?.email || 'Guest'}`}
                                            alt="User"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#F59E0B] rounded-md border-[1.5px] border-[#171717] flex items-center justify-center shadow-sm">
                                        <Plus size={10} className="text-black stroke-[4]" />
                                    </div>
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => onLoginRequest('login')}
                                className="text-sm font-bold text-white tracking-tight hover:text-[#F59E0B] transition-colors px-4 py-2"
                            >
                                Acceder
                            </button>
                            <button
                                onClick={() => onLoginRequest('register')}
                                className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black text-sm font-bold px-6 py-2.5 rounded-xl transition-all active:scale-95 shadow-lg shadow-[#F59E0B]/10"
                            >
                                Registrarse
                            </button>
                        </div>
                    )}
                </div>
            </nav>

            <div className="flex flex-1 overflow-hidden relative">

                {/* 2. DESKTOP SIDEBAR (ASIDE) */}
                <aside className="hidden lg:flex w-64 flex-col bg-[#171717] border-r border-white/5 overflow-y-auto no-scrollbar shrink-0 pt-6 px-4">

                    {/* Depositar CTA */}
                    <button
                        onClick={() => setActiveTab('deposit')}
                        className="w-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 hover:bg-[#F59E0B] hover:text-black transition-all p-4 rounded-2xl flex items-center justify-between group mb-8"
                    >
                        <div className="flex items-center gap-3">
                            <div className="bg-[#F59E0B] text-black p-2 rounded-xl group-hover:bg-black group-hover:text-[#F59E0B] transition-colors">
                                <Wallet size={18} />
                            </div>
                            <span className="text-sm font-bold tracking-tight">Depositar</span>
                        </div>
                        <ChevronRight size={14} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>

                    {/* Main Nav Removed - Moved to Header */}

                    {/* Popular Sports Section */}
                    <div className="space-y-6 mb-10">
                        <div className="px-4 flex items-center justify-between">
                            <h3 className="text-xs font-bold text-white tracking-tight">Deportes Populares</h3>
                            <Star size={10} className="text-[#F59E0B] opacity-50" />
                        </div>
                        <div className="space-y-1">
                            {popularSports.map(sport => (
                                <button
                                    key={sport.id}
                                    onClick={() => setActiveTab('sports', { sportId: sport.id })}
                                    className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-white hover:bg-white/5 transition-all group"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-neutral-900 border border-white/5 flex items-center justify-center group-hover:border-[#F59E0B]/30 transition-all">
                                        <sport.Icon size={14} className="opacity-40 group-hover:opacity-100 group-hover:text-[#F59E0B]" />
                                    </div>
                                    <span className="text-xs font-bold tracking-tight">{sport.label}</span>
                                    <ChevronRight size={12} className="ml-auto opacity-0 group-hover:opacity-20 transition-all" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Others Section */}
                    <div className="space-y-6 pb-20">
                        <div className="px-4">
                            <h3 className="text-xs font-bold text-white tracking-tight">Otros Deportes</h3>
                        </div>
                        <div className="space-y-1">
                            {otherSports.map(sport => (
                                <button
                                    key={sport.label}
                                    onClick={() => setActiveTab('sports', { sportName: sport.label })}
                                    className="w-full flex items-center gap-4 px-4 py-2.5 rounded-xl text-white hover:bg-white/5 transition-all group cursor-pointer"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-neutral-900 border border-white/5 flex items-center justify-center group-hover:border-[#F59E0B]/30 transition-all">
                                        <sport.Icon size={12} className="opacity-40 group-hover:opacity-100 group-hover:text-[#F59E0B]" />
                                    </div>
                                    <span className="text-xs font-bold tracking-tight">{sport.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* 3. MAIN CONTENT AREA */}
                <main className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
                    <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden scroll-smooth no-scrollbar relative pt-0 pb-[70px] lg:pt-0 lg:pb-0">
                        {children}
                        <Footer />
                    </div>
                </main>
            </div>

            {/* 4. MOBILE BOTTOM NAV (Fixed on bottom) */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#171717] border-t border-white/5 px-2 flex justify-around items-stretch z-[1000] h-[70px] pb-safe">
                {navItems.filter(i => i.id !== 'profile').map(item => (
                    <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        className="flex-1 flex flex-col items-center justify-center relative transition-all active:scale-95"
                    >
                        {activeTab === item.id && (
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-[#F59E0B] shadow-[0_0_15px_rgba(245,158,11,0.6)] rounded-b-sm animate-in fade-in zoom-in duration-300"></div>
                        )}

                        <div className={`flex flex-col items-center gap-1 transition-all duration-300 ${activeTab === item.id ? 'text-[#F59E0B]' : 'text-neutral-300'}`}>
                            {item.id === 'casino' ? (
                                <CasinoIcon
                                    size={activeTab === item.id ? 24 : 20}
                                    strokeWidth={activeTab === item.id ? 2.5 : 2}
                                    className="transition-all"
                                />
                            ) : (
                                <item.icon
                                    size={20}
                                    strokeWidth={activeTab === item.id ? 2.8 : 2}
                                />
                            )}
                            <span className={`text-[10px] font-bold tracking-tight ${activeTab === item.id ? 'opacity-100' : 'opacity-80'}`}>
                                {item.label}
                            </span>
                        </div>
                    </button>
                ))}
            </nav>

            {/* SOPORTE HÍBRIDO (Bot/Humano) — flota en todas las pantallas del usuario */}
            <LiveChatWidget user={user} userProfile={userProfile} />
        </div >
    );
};

export default UserLayout;
